import React from 'react'
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCol,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CRow,
} from '@coreui/react-pro'
import { formatRangeLabel } from './common'

export const PRESETS = [
  { key: 'today', label: 'Today', days: 1 },
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '28d', label: 'Last 28 days', days: 28 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: 'custom', label: 'Custom', days: null },
]

/** Cap enforced by the API. Mirrored here so the admin is told before they ask. */
export const MAX_RANGE_DAYS = 92

const toIso = (date) => date.toISOString().slice(0, 10)

/**
 * Resolve a preset into from/to. Dates are the ADMIN's local dates and the API
 * interprets them in the game's timezone, which is displayed alongside.
 */
export const rangeForPreset = (presetKey) => {
  const preset = PRESETS.find((p) => p.key === presetKey)
  if (!preset || !preset.days) return null

  const today = new Date()
  const to = toIso(today)
  const start = new Date(today)
  start.setDate(start.getDate() - (preset.days - 1))

  return { from: toIso(start), to }
}

export const daysBetween = (from, to) => {
  if (!from || !to) return 0
  const a = Date.parse(`${from}T00:00:00Z`)
  const b = Date.parse(`${to}T00:00:00Z`)
  if (Number.isNaN(a) || Number.isNaN(b)) return 0
  return Math.floor((b - a) / 86400000) + 1
}

const FilterBar = ({ filters, onChange, appVersions, meta, rangeError }) => {
  const setPreset = (key) => {
    if (key === 'custom') {
      onChange({ ...filters, preset: 'custom' })
      return
    }
    const range = rangeForPreset(key)
    onChange({ ...filters, preset: key, from: range.from, to: range.to })
  }

  const setCustomDate = (field, value) => {
    onChange({ ...filters, preset: 'custom', [field]: value })
  }

  const spanDays = daysBetween(filters.from, filters.to)

  return (
    <CCard className="mb-4 av2-filterbar">
      <CCardBody className="py-3">
        <CRow className="g-3 align-items-end">
          <CCol xs={12} xl={5}>
            <CFormLabel className="small text-medium-emphasis mb-1">Date range</CFormLabel>
            <div className="av2-scroll-x">
              <CButtonGroup role="group" aria-label="Date range presets">
                {PRESETS.map((preset) => (
                  <CButton
                    key={preset.key}
                    color="primary"
                    variant={filters.preset === preset.key ? undefined : 'outline'}
                    size="sm"
                    onClick={() => setPreset(preset.key)}
                  >
                    {preset.label}
                  </CButton>
                ))}
              </CButtonGroup>
            </div>
          </CCol>

          {filters.preset === 'custom' && (
            <>
              <CCol xs={6} md={3} xl={2}>
                <CFormLabel className="small text-medium-emphasis mb-1" htmlFor="av2-from">
                  From
                </CFormLabel>
                <CFormInput
                  id="av2-from"
                  type="date"
                  size="sm"
                  value={filters.from || ''}
                  max={filters.to || undefined}
                  onChange={(e) => setCustomDate('from', e.target.value)}
                />
              </CCol>
              <CCol xs={6} md={3} xl={2}>
                <CFormLabel className="small text-medium-emphasis mb-1" htmlFor="av2-to">
                  To
                </CFormLabel>
                <CFormInput
                  id="av2-to"
                  type="date"
                  size="sm"
                  value={filters.to || ''}
                  min={filters.from || undefined}
                  onChange={(e) => setCustomDate('to', e.target.value)}
                />
              </CCol>
            </>
          )}

          <CCol xs={12} md={6} xl={filters.preset === 'custom' ? 3 : 4}>
            <CFormLabel className="small text-medium-emphasis mb-1">Platform</CFormLabel>
            <div className="av2-scroll-x">
              <CButtonGroup role="group" aria-label="Platform">
                {[
                  { key: 'all', label: 'All' },
                  { key: 'ios', label: 'iPhone' },
                  { key: 'android', label: 'Android' },
                ].map((option) => (
                  <CButton
                    key={option.key}
                    color="secondary"
                    variant={filters.platform === option.key ? undefined : 'outline'}
                    size="sm"
                    onClick={() => onChange({ ...filters, platform: option.key })}
                  >
                    {option.label}
                  </CButton>
                ))}
              </CButtonGroup>
            </div>
          </CCol>

          <CCol xs={12} md={6} xl={3}>
            <CFormLabel className="small text-medium-emphasis mb-1" htmlFor="av2-version">
              App version
            </CFormLabel>
            <CFormSelect
              id="av2-version"
              size="sm"
              value={filters.appVersion || 'all'}
              onChange={(e) => onChange({ ...filters, appVersion: e.target.value })}
            >
              <option value="all">All versions</option>
              {(appVersions || []).map((row) => (
                <option key={`${row.app_version}-${row.build_number}`} value={row.app_version}>
                  {row.app_version}
                  {row.build_number ? ` (build ${row.build_number})` : ''}
                </option>
              ))}
            </CFormSelect>
          </CCol>
        </CRow>

        {/* Numbers without a stated period and clock invite mistrust. */}
        <div className="d-flex flex-wrap gap-2 align-items-center mt-3 small text-medium-emphasis">
          {meta?.from && meta?.to && (
            <span>
              Showing <strong>{formatRangeLabel(meta.from, meta.to)}</strong>
              {meta.previous_from && (
                <> · compared with {formatRangeLabel(meta.previous_from, meta.previous_to)}</>
              )}
            </span>
          )}
          {meta?.timezone && (
            <span className="av2-chip" title="All days are counted on the game's own clock">
              Times shown in {meta.timezone.replace(/_/g, ' ')}
            </span>
          )}
          <span className="av2-chip" title="Offline devices upload queued events for up to 7 days">
            Last 2 days are still settling
          </span>
        </div>

        {rangeError && <div className="small text-danger mt-2">{rangeError}</div>}
        {!rangeError && spanDays > MAX_RANGE_DAYS && (
          <div className="small text-danger mt-2">
            Please choose a range of {MAX_RANGE_DAYS} days or fewer. You have selected {spanDays}{' '}
            days.
          </div>
        )}
      </CCardBody>
    </CCard>
  )
}

export default FilterBar
